import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { SliceState, Statusable } from '../../interfaces';
import { ServerCategoryInventoryItem } from '../../hooks/InventoryHooks/useCategoryInventory';
import { toNumber } from '@rahtab/system/lib/tools/string';
import { Category } from './categoriesSlice';
import { RootState } from 'rahtab-redux';

const initialState: CategoryInventorySliceState = {
  loading: 'idle',
  categoryInventory: {},
};

const CategoryInventorySlice = createSlice({
  name: 'categoryInventory',
  initialState,
  reducers: {
    setCategoryInventoryLoading(state, { payload }: PayloadAction<CategoryInventorySliceState['loading']>) {
      state.loading = payload;
    },
    addCategoryInventory(state, { payload }: PayloadAction<ServerCategoryInventoryItem>) {
      const {
        ProductCode: categoryId,
        ActiveLoadCount: activeLoadCount,
        TemplateCount: merchandiseCount,
        StatusCode: status,
        Status: statusText,
      } = payload;
      const category: CategoryInventory = {
        activeLoadCount: toNumber(activeLoadCount),
        merchandiseCount: toNumber(merchandiseCount),
        status:
          (parseInt(status, 10) as 0 | 1) === 0 ? CategoryInventoryStatus.Inactive : CategoryInventoryStatus.Active,
        statusText,
      };
      state.categoryInventory[categoryId] = category;
    },
  },
});

const { actions, reducer } = CategoryInventorySlice;

export const { addCategoryInventory } = actions;

export default reducer;

export function inventoryCategorySelector(ids: string): (state: RootState) => Category & CategoryInventory;
export function inventoryCategorySelector(ids: string[]): (state: RootState) => Array<Category & CategoryInventory>;
export function inventoryCategorySelector(ids: string | string[]) {
  return (state: RootState) => {
    const { category, categoryInventory } = state;
    if (typeof ids === 'string') return getInventoryCategory(ids);
    return ids.map((id) => getInventoryCategory(id));
    function getInventoryCategory(id: string): Category & CategoryInventory {
      return {
        ...category.categories[id],
        ...categoryInventory.categoryInventory[id],
      };
    }
  };
}
export const CategoryInventoryStatus = {
  Inactive: 0,
  Active: 1,
} as const;

export interface CategoryInventory extends Statusable<typeof CategoryInventoryStatus> {
  merchandiseCount: number;
  activeLoadCount: number;
}

type CategoryInventorySliceState = SliceState<'categoryInventory', CategoryInventory>;
